import { useCallback, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import AppDialogTitle from '../modals/AppDialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import styled from '@mui/system/styled';
import Button from '@mui/material/Button';
import Avatar from 'boring-avatars';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import type { AnyWallet } from '@/stores/Connectors';
import { enqueueSnackbar } from 'notistack';

const WalletIconOrigin = styled('img')({
	width: 20,
	marginLeft: 4,
});

const WalletIconOther = styled('img')({
	width: 24,
	marginRight: 8,
});

const Container = styled(Box)({
	display: 'flex',
	alignItems: 'center',
});

type AccountModalProps = {
	vmType: 'evm' | 'solana' | 'sui';
	wallet: AnyWallet;
	onClose: () => void;
	onChangeWallet: () => void;
};

const AccountModal = observer(function AccountModal({ vmType, wallet, onClose, onChangeWallet }: AccountModalProps) {
	const store = useStore();
	const address = wallet.selectedAddress;

	const displayAddress = useMemo<string>(() => {
		if (address) {
			return `${address.slice(0, 6)}...${address.slice(address.length - 4)}`;
		}
		return '';
	}, [address]);

	const otherWallets = useMemo(() => {
		return store.wallets.connectedWallets.filter(
			(w) => w !== wallet && w.type === vmType,
		);
	}, [store.wallets.connectedWallets, wallet, vmType]);

	const handleDisconnect = async () => {
		try {
			await wallet.disconnect();
		} catch (err) {
			console.error(err);
			return;
		}
		onClose();
	};

	const disconnectWallet = useCallback(async (w: AnyWallet) => {
		try {
			await w.disconnect();
		} catch (err: any) {
			enqueueSnackbar(err?.reason || err?.message || 'Unknown error', { variant: 'error' });
			console.error(err);
		}
	}, []);

	if (!address) return null;

	return (
		<Dialog open onClose={onClose}>
			<AppDialogTitle title="Account" onClose={onClose} />
			<DialogContent>
				<Box sx={{ width: 1, px: 0.5, mt: 1, mb: 3 }}>
					<Box
						sx={{
							width: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: 1,
						}}
					>
						<Container>
							<Avatar
								size={48}
								name={address}
								variant="bauhaus"
								colors={['#FFE600', '#248EED', '#A8EEFF', '#A7BEFE', '#875AFF']}
							/>
							<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', ml: 2 }}>
								<Typography variant="appMediumRegular" color="common.textPrimary">
									{displayAddress}
								</Typography>
								<Container>
									<Typography variant="appTinyRegular" color="common.N150" sx={{ mr: 0.5 }}>
										{wallet.name}
									</Typography>
									{wallet.icon && <WalletIconOrigin src={wallet.icon} alt="." />}
								</Container>
							</Box>
						</Container>
						<Container sx={{ flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
							<Button size="small" variant="outlined" onClick={onChangeWallet}>
								Change Wallet
							</Button>
							{wallet.disconnectable && (
								<Button size="small" variant="outlined" onClick={handleDisconnect}>
									Disconnect
								</Button>
							)}
						</Container>
					</Box>

					{otherWallets.length > 0 && (
						<>
							<Box sx={{ mt: 3 }}>
								<Typography variant="appMediumBold" color="common.textPrimary">Other Wallets</Typography>
							</Box>
							{otherWallets.map((w) => (
								<Container
									key={`${w.type}-${w.name}`}
									sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 2, width: 1 }}
								>
									<Container>
										{w.icon && <WalletIconOther src={w.icon} alt="." />}
										<Typography variant="appSmallRegular" color="common.N150" sx={{ mr: 1 }}>
											{w.name}
										</Typography>
										<Typography variant="appSmallRegular" color="common.textPrimary">
											{w.selectedAddress?.slice(0, 6)}...{w.selectedAddress?.slice(-4)}
										</Typography>
									</Container>
									{w.disconnectable && (
										<Button size="small" variant="outlined" onClick={() => disconnectWallet(w)}>
											Disconnect
										</Button>
									)}
								</Container>
							))}
						</>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
});

export default AccountModal;
